import { cva, type VariantProps } from 'class-variance-authority';
import type { DetailedHTMLProps, FC, HTMLAttributes, SVGProps } from 'react';

import { cn } from '@/lib/utils';

export type IconSizeType =
  | 'xxxsmall'
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge'
  | 'xxxlarge';

const iconVariants = cva(
  'inline-flex items-center antialiased [text-rendering:optimizeLegibility] [&>svg]:shrink-0',
  {
    variants: {
      size: {
        xxxsmall: '[&>svg]:size-3',
        xxsmall: '[&>svg]:size-3.5',
        xsmall: '[&>svg]:size-4',
        small: '[&>svg]:size-5',
        medium: '[&>svg]:size-6',
        large: '[&>svg]:size-7',
        xlarge: '[&>svg]:size-8',
        xxlarge: '[&>svg]:size-9',
        xxxlarge: '[&>svg]:size-11',
      },
      themeColor: {
        success: 'text-green-500',
        error: 'text-red-500',
        warning: 'text-yellow-500',
        info: 'text-aqua-500',
        default: 'text-gray-900',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
);

type IconVariantProps = VariantProps<typeof iconVariants>;

type Props = DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> & {
  size?: IconSizeType;
  color?: 'success' | 'error' | 'warning' | 'info' | 'default' | string;
  icon: FC<SVGProps<SVGSVGElement>>;
};

const isThemeColor = (c?: string): c is NonNullable<IconVariantProps['themeColor']> =>
  c === 'success' || c === 'error' || c === 'warning' || c === 'info' || c === 'default';

const Icon = ({
  className,
  style,
  tabIndex,
  size = 'medium',
  color,
  icon: IconComponent,
  onClick,
  ...rest
}: Props) => {
  const hasThemeColor = color != null && isThemeColor(color);

  let computedTabIndex = tabIndex;
  if (computedTabIndex === undefined && onClick) {
    computedTabIndex = -1;
  }

  return (
    <span
      {...rest}
      role="img"
      className={cn(
        iconVariants({
          size,
          ...(hasThemeColor ? { themeColor: color } : {}),
        }),
        className,
      )}
      style={{
        color: color != null && !hasThemeColor ? color : undefined,
        ...style,
      }}
      tabIndex={computedTabIndex}
      onClick={onClick}
    >
      <IconComponent />
    </span>
  );
};

export default Icon;
